import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, of, combineLatest } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DecryptService } from './decrypt.service';
import WMTSCapabilities from 'ol/format/WMTSCapabilities.js';
import { UserService } from '../services/user.service';
import { LayerType, mapLayer } from '../interfaces/common';

@Injectable({
  providedIn: 'root'
})

export class AppLoadService {
  private settings: any = null;
  private urlParams: Map<string, any> = new Map();
  private wmtsText: any = {};
  private URLsThatFailed: string[] = [];   // URLs where the getCapabilities request failed
  private mapLayers: mapLayer[] = [];
  private didSomeLayerLoadFail: boolean = false;
  private layersThatFailed: object = {};  //Contains string of layer that failed in english/icelandic
  private authGroupInfo: any;
  private isIOS: boolean = false;

  constructor(private http: HttpClient, 
              private decryptService: DecryptService,
              private userService: UserService) {}

  initializeApp() {
    return new Promise((resolve) => {
      this.isIOS = this.isDeviceIOS();
      this.urlParams = this.resolveURLParameters()
      const thisApplication: string = this.urlParams.get("application")
      const url = ''.concat(environment.bendURL, '/gaic/', thisApplication, '/cf');
      const settingsObservable$: Observable<any> = this.http.get(url);

      /* 
        Here we are getting the settings package for mapview. When that request is completed then we
        get the WMTS capabilities which is neccessary for this type of layer. We do it after because the requests to get
        WMTS capabilities is dependent on information found in the settings(switchMap operator). These WMTS-capabilities requests might be 0 or many,
        depending on the layers found in settings. The results of these request are processed once they are all completed(combineLatest operator)
        
        The WMTS capabilities objects are stored in this.wmtsText, which then gets
        sent down to map-component where it is used in __load_wmts().
      */
      settingsObservable$.pipe(
        switchMap( encryptedSettings => {
          const settings: any = this.decryptService.decryptCF(encryptedSettings.lets_start_the_show)
          this.settings = settings
          this.mapLayers = settings.mapLayers
          //Set the serverVersion that come with the CF network call
          environment.serverVersion = encryptedSettings?.version

          const combinedWMTSObservables$ = this.getCapabilitiesDocument();
          //if userData.User comes with the initial settings, then a user is already logged in
          if (this.settings?.userData?.User) {
            this.userService.fillInUserData(this.settings.userData)
          }
          
          //No WMTS layers or no capabilites documents to fetch. resolve promise and continue on with the application
          if (combinedWMTSObservables$ === undefined) {
            resolve(true)
            return of([]);
          }

          return combinedWMTSObservables$
        })
      ).subscribe((combinedWMTSObsResults: any[]) => {
        this.wmtsText = this.parseWMTSCapabilitiesDocument(combinedWMTSObsResults)
        resolve(true)
        // Error calback, we do nothing here since we use catchError above.
      },
        (error) => {
          console.error("Error in settings subscription", error);
        },
        // The "complete" callback, called when this combineLatest observable is finished
        () => {
          this.findLayersThatErrored()
        }
      )
    });
  }

  public getSettings(): any { return this.settings; }

  public getUrlParams(): Map<string, any> { return this.urlParams; }

  public getWMTSText(): any { return this.wmtsText; }

  /**
   * Parse the the raw capabilities document to a JS-object and returns it
   * @param combinedWMTSObsResults 
   * @returns 
   */
  public parseWMTSCapabilitiesDocument(combinedWMTSObsResults: any[]): { [key: string]: any } {
    const parser = new WMTSCapabilities();
    const WMTSKeys: string[] = Object.keys(this.wmtsText)
    const newWMTSObj: { [key: string]: any } = {};
  
    combinedWMTSObsResults.forEach((text: string) => {
      const result: object = parser.read(text);
      const key = WMTSKeys.shift();
      if (key) {
        newWMTSObj[key] = result;
      }
    });
  
    return newWMTSObj;
  }    

  /**
   * Should try to get the capabilites document for WMTS layers. and then finally populate these documents
    in this.wmtsText. Returns a combineLatest observable. If there are no WMTS layers where we need to fetch
    capabilities documents then it returns undefined.
   * @returns 
   */
  public getCapabilitiesDocument(): Observable<any[]> | undefined {
    const WMTSurlSet: Set<string> = new Set()
    const headers: any = {}
    for (const layer of this.mapLayers) {
      if (layer.type === LayerType.WMTS) {
        this.wmtsText[layer.url] = undefined;
        const wmtsurl = ''.concat(layer.url, '?service=wmts&request=GetCapabilities');
        WMTSurlSet.add(wmtsurl)
        
        let h: HttpHeaders = new HttpHeaders()

        if ((layer.option !== null) && ("Authorization" in layer.option)) {
          const user = layer.option.Authorization["username"];
          const pass = layer.option.Authorization["password"];

          if (user !== undefined && pass !== undefined) {
            h = h.append('Authorization', 'Basic ' + btoa(user + ':' + pass)) 
            headers[layer.url] = h
          } 
        }
      } 
    };
    
    const WMTSObservables: Observable<any>[] = []
    for (const uniqueURL of WMTSurlSet) {
      const maybeyHeader: HttpHeaders | undefined = headers[uniqueURL.split("?")[0]];
      const httpOptions: { responseType: "text", headers?: HttpHeaders } = { responseType: "text" };
      if (maybeyHeader !== undefined) httpOptions["headers"] = maybeyHeader
      const WMTSObs$: Observable<any> = this.http.get(uniqueURL, httpOptions).pipe(
      /*
        catchError catches the error and returns undefined instead. This is done here so that
        even if one getCapabilities request fails, we handle that error and the app can continue its 
        execution. If there is a error in request undefined is returned and we continue on to the next request
        if there is any. of() simply creates a observable that emits immediately the argument value and completes.
      */
        catchError(
          (error) => {
            console.warn(`GetCapabilites request to ${uniqueURL} failed`, error)
            //Push url to URLsThatFailed that had a failure in the request
            this.URLsThatFailed.push(uniqueURL)
            return of(undefined)
          }
        )
      )
      WMTSObservables.push(WMTSObs$)
    }

    /*
      If WMTSObservables is empty it means there are no WMTS layers int his application.
      If so then just go straigth to resolve, we dont need to process anything for this.wmtsText.
      If this was not here, then an application with no WMTS layers would hang and nothing is displayed
      since the subsequent subscribe would never fire since im passing an empty array into
      combineLatest operator.
    */
    if (WMTSObservables.length == 0) {
      return undefined;
    }

    const combinedWMTSObservables$: Observable<any[]> = combineLatest(WMTSObservables)
    return combinedWMTSObservables$
  }

  /**
   * Inform user if some getCapabilities request failed. Would be good to inform what what layers are affected.
   * @returns 
   */
  private findLayersThatErrored(): void {
    if (this.URLsThatFailed.length == 0) return; //No URLs failed, so return early here

    this.didSomeLayerLoadFail = true
    let layerErrorStrEN: string = ""
    let layerErrorStrIS: string = ""

    this.URLsThatFailed.forEach( (url) => {
      //Split at the "?" so that we can find which layers are affected by the error
      const layerURL: string = url.split("?")[0]
      //console.log("Layer URL that failed: " + layerURL)

      //Find all layers that are not available due to error
      //const layersWithError: mapLayer[] = this.mapLayers.filter( (ml) => ml.url == layerURL )
      const layersWithError = this.mapLayers.filter(ml => ml.url === layerURL || (ml.url && ml.url.includes(layerURL)));
      const layerErrorNamesEN: string[] = layersWithError.map( (lwe) => lwe.title_lang["en"] )
      const layerErrorNamesIS: string[] = layersWithError.map( (lwe) => lwe.title_lang["is"] )

      const singlelayerErrorStrEN: string = layerErrorNamesEN.join(", ")
      const singlelayerErrorStrIS: string = layerErrorNamesIS.join(", ")

      layerErrorStrEN += singlelayerErrorStrEN + ", "
      layerErrorStrIS += singlelayerErrorStrIS + ", "

    })
    //Taking out the last ", " in the string so that it is not in the end
    layerErrorStrEN = layerErrorStrEN.slice(0, layerErrorStrEN.length - 2)
    layerErrorStrIS = layerErrorStrIS.slice(0, layerErrorStrIS.length - 2)

    this.layersThatFailed = {"is": layerErrorStrIS, "en": layerErrorStrEN}
    //window.alert(errorInfoString)
  }

  public getAuthGroupInfo() : Observable<object>{
    return this.authGroupInfo;
  }

  public setAuthGroupInfo(group: string) {
    if (!group || group.trim() === '') {
      return null;
    }
    const url = environment.authGroupURL + group;
    this.http.get(url).pipe(
      catchError((error: any) => {
        console.error("Error setting auth group info", error);
        return of(null);
      })
    ).subscribe(
      (data) => {
        this.authGroupInfo = data;
      }
    );
  }
  
  public didSomeLayerFail(): boolean {
    return this.didSomeLayerLoadFail
  }

  public getFailedLayers(): object {
    return this.layersThatFailed
  }

  private isDeviceIOS(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua) || (ua.includes('mac') && 'ontouchend' in document);
  }

  public getIsDeviceIOS(): boolean {
    return this.isIOS;
  }

  /**
   * Reads the URL searh parameters, converts alias keys to full name keys,
   * parses each calue into correct JS type, returns a Map where the key is the full name key and value is the parsed value
   * @returns Map<string, any> 
   */
  private resolveURLParameters(): Map<string, any> {
    //Default application if not set but URL is "kortasja"
    const outputURLParams: Map<string, any> = new Map([["application", "kort"]])

    const URLSearchParameters: URLSearchParams = new URLSearchParams(window.location.search)
    //Sometimes the URL will use aliases(to decrese URL length), this is a mapping between the alias and its value
    const URLSearchParametersAliases: Map<string, string> = new Map([
      ["app", "application"],
      ["l", "lang"],
      ["z", "zoom"],
      ["c", "center"],
      ["ls", "layers"],
      ["op", "opacity"],
      ["m", "marker"],
      ["eg", "expandedGroups"]
    ])

    for (const [key, value] of URLSearchParameters.entries()) {
      //console.log(`${key} -> ${value}`);
      const aliasValue: string | undefined = URLSearchParametersAliases.get(key)
      //If aliasValue is not being used it will be undefined, else if it is used it will have some value
      if (aliasValue) outputURLParams.set(aliasValue, this.parseURLParamsValue(aliasValue, value))
      else outputURLParams.set(key, this.parseURLParamsValue(key, value))
    }

    return outputURLParams
  }

  /**
   * Parses values from query-part og the URL to correct type. They are all strings to begin with
   * but some of them need to be converted to number, arrays of number, maps...
   * @param key string
   * @param value string
   * @returns string | number | number[] | object | null
   */
  private parseURLParamsValue(key: string, value: string): string | number | number[] | object | null {
    //comma seperated values to array
    if (["center", "layers", "expandedGroups"].includes(key)) {
      const arrayValue: (number | string)[] = value.split(",").map((val: string) => {
        //Special case "_s" stands for a stuck layer, it should be stuck in the active layers group
        if (val.includes("_s")) {
          return val
        }
        return Number(val)
      })

      if (key === "center" && arrayValue.length !== 2) {
        return null
      }
      
      return arrayValue
    }

    if (key === "zoom") {
      return Number(value)
    }

    if (key === "opacity") {
      const opacityValues: string[] = value.split(',');
      const opacityMap: { [key: string]: number } = {};

      for (const opacityValue of opacityValues) {
        const [layerId, opacity] = opacityValue.split('_');
        if (layerId && opacity) {
          opacityMap[layerId] = parseFloat(opacity);
        }
      }
      return Object.keys(opacityMap).length === 0 ? null : opacityMap;
    }
    
    return value  // If we have reached here, then its just a regular string value we want to return
  }
}